import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/base/Modal";
import type { ThemeClasses } from "../../../../types/components";
import type { User } from "../../../../store/admin/usersStore";

interface ChangePasswordModalProps {
  user: User | null;
  themeClasses: ThemeClasses;
  onClose: () => void;
  onConfirm: (password: string) => void;
  loading: boolean;
}

export default function ChangePasswordModal({
  user,
  themeClasses,
  onClose,
  onConfirm,
  loading,
}: ChangePasswordModalProps) {
  const { t } = useTranslation("adminSections");
  const [password, setPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!password || password.trim() === "") {
      setError(t("users.actions.passwordRequired") || "Por favor ingrese una contraseña");
      return;
    }

    if (password !== verifyPassword) {
      setError(t("users.actions.passwordMismatch") || "Las contraseñas no coinciden");
      return;
    }

    if (password.length < 6) {
      setError(t("users.actions.passwordMinLength") || "La contraseña debe tener al menos 6 caracteres");
      return;
    }

    onConfirm(password);
  };

  const handleClose = () => {
    setPassword("");
    setVerifyPassword("");
    setError("");
    onClose();
  };

  if (!user) return null;

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className={`${themeClasses.textSecondary} mb-4`}>
        {t("users.actions.changePasswordFor") || "Cambiar contraseña para"}{" "}
        <strong className={themeClasses.text}>
          {user.first_name} {user.last_name}
        </strong>
      </p>

      <div>
        <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
          {t("users.actions.newPassword") || "Nueva contraseña"}
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          className={`w-full px-4 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.bg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          placeholder={t("users.actions.enterNewPassword") || "Ingrese la nueva contraseña"}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
          {t("users.actions.verifyPassword") || "Verificar contraseña"}
        </label>
        <input
          type="password"
          value={verifyPassword}
          onChange={(e) => setVerifyPassword(e.target.value)}
          disabled={loading}
          className={`w-full px-4 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.bg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed`}
          placeholder={t("users.actions.verifyPasswordPlaceholder") || "Verifique la contraseña"}
        />
      </div>

      {error && (
        <div className="text-red-500 text-sm">{error}</div>
      )}

      <div className="flex gap-3 justify-end pt-4">
        <button
          type="button"
          onClick={handleClose}
          disabled={loading}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {t("users.actions.cancel") || "Cancelar"}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 rounded-lg font-medium bg-primary-500 hover:bg-primary-400 text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (t("users.actions.changing") || "Cambiando...") : (t("users.actions.change") || "Cambiar")}
        </button>
      </div>
    </form>
  );

  return (
    <Modal
      content={formContent}
      title={t("users.actions.changePassword") || "Cambiar contraseña"}
      themeClasses={themeClasses}
      onClose={handleClose}
      width="max-w-md"
    />
  );
}
